import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash, LockSimple, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { STATUS_GROUPS, getStatuses } from "@/lib/gantt-utils";

const GROUP_HINT = {
  not_started: "Work that hasn't begun yet.",
  in_progress: "Active work. Flag a status as a blocker to count it under Blockers.",
  done: "Finished work, whatever the outcome.",
};

export default function StatusesDialog({ open, onOpenChange, project, tasks, canEdit, onSave }) {
  const [statuses, setStatuses] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setStatuses(JSON.parse(JSON.stringify(getStatuses(project))));
  }, [open, project]);

  const usage = useMemo(() => {
    const m = {};
    (tasks || []).forEach((t) => { if (t.status) m[t.status] = (m[t.status] || 0) + 1; });
    return m;
  }, [tasks]);

  const original = getStatuses(project);
  const originalLabel = Object.fromEntries(original.map((s) => [s.id, s.label]));
  const keptIds = new Set(statuses.map((s) => s.id));
  const keptLabels = new Set(statuses.map((s) => s.label.trim()));
  const clearedCount = original
    .filter((s) => !keptIds.has(s.id) && !keptLabels.has(s.label))
    .reduce((n, s) => n + (usage[s.label] || 0), 0);

  const update = (id, patch) => setStatuses((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const remove = (id) => setStatuses((prev) => prev.filter((s) => s.id !== id));
  const add = (group) =>
    setStatuses((prev) => [
      ...prev,
      { id: `st_${Date.now().toString(36)}`, label: "New status", group, color: "#64748b", blocker: false },
    ]);

  const save = async () => {
    const labels = statuses.map((s) => s.label.trim().toLowerCase());
    if (labels.some((l) => !l)) return toast.error("Every status needs a name");
    if (new Set(labels).size !== labels.length) return toast.error("Status names must be unique");
    setSaving(true);
    try {
      await onSave(statuses.map((s) => ({ ...s, label: s.label.trim() })));
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-2xl p-0 gap-0 overflow-hidden" data-testid="statuses-dialog">
        <DialogHeader className="p-6 pb-4 border-b border-slate-200">
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Manage statuses</DialogTitle>
          <DialogDescription className="text-slate-500">
            Rename, add or remove statuses. The three groups are fixed and drive the dashboard counts.
          </DialogDescription>
        </DialogHeader>
        <div className="p-6 max-h-[65vh] overflow-y-auto custom-scrollbar space-y-6">
          {STATUS_GROUPS.map((g) => (
            <StatusGroup
              key={g.key}
              group={g}
              items={statuses.filter((s) => s.group === g.key)}
              usage={usage}
              originalLabel={originalLabel}
              canEdit={canEdit}
              onAdd={() => add(g.key)}
              onUpdate={update}
              onRemove={remove}
            />
          ))}
        </div>
        <DialogFooter className="p-4 border-t border-slate-200 flex items-center sm:justify-between gap-3">
          <div className="text-xs text-amber-700 flex items-center gap-1.5" data-testid="statuses-clear-warning">
            {clearedCount > 0 && (
              <>
                <WarningCircle size={14} weight="bold" />
                Saving clears the status on {clearedCount} task{clearedCount === 1 ? "" : "s"}. Nothing else changes.
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-md" onClick={() => onOpenChange(false)} data-testid="statuses-cancel-btn">
              Cancel
            </Button>
            {canEdit && (
              <Button onClick={save} disabled={saving} className="rounded-md bg-slate-950 hover:bg-slate-800 text-white" data-testid="statuses-save-btn">
                {saving ? "Saving…" : "Save statuses"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusGroup({ group, items, usage, originalLabel, canEdit, onAdd, onUpdate, onRemove }) {
  return (
    <section data-testid={`status-group-${group.key}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-semibold text-slate-950 flex items-center gap-1.5">
            <LockSimple size={12} weight="bold" className="text-slate-400" />
            {group.label}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{GROUP_HINT[group.key]}</div>
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={onAdd} className="rounded-md h-8" data-testid={`add-status-${group.key}`}>
            <Plus size={12} weight="bold" /><span className="ml-1.5">Add status</span>
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-md p-3">No statuses in this group.</div>
        )}
        {items.map((s) => (
          <StatusRow key={s.id} status={s} used={usage[originalLabel[s.id] ?? s.label] || 0} canEdit={canEdit} onUpdate={onUpdate} onRemove={onRemove} />
        ))}
      </div>
    </section>
  );
}

function StatusRow({ status, used, canEdit, onUpdate, onRemove }) {
  return (
    <div className="flex items-center gap-2 p-2 border border-slate-200 rounded-md bg-white" data-testid={`status-row-${status.id}`}>
      <input
        type="color"
        value={status.color}
        onChange={(e) => onUpdate(status.id, { color: e.target.value })}
        disabled={!canEdit}
        className="h-8 w-8 rounded-md border border-slate-300 cursor-pointer shrink-0"
        data-testid={`status-color-${status.id}`}
      />
      <Input
        value={status.label}
        maxLength={40}
        onChange={(e) => onUpdate(status.id, { label: e.target.value })}
        disabled={!canEdit}
        className="h-8 rounded-md border-slate-300 text-sm flex-1"
        data-testid={`status-label-${status.id}`}
      />
      <span className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-400 w-16 text-right shrink-0">
        {used} task{used === 1 ? "" : "s"}
      </span>
      {status.group === "in_progress" && (
        <button
          type="button"
          disabled={!canEdit}
          onClick={() => onUpdate(status.id, { blocker: !status.blocker })}
          aria-pressed={!!status.blocker}
          className={`h-8 px-2.5 rounded-md border text-[11px] font-medium inline-flex items-center gap-1 shrink-0 transition-colors ${
            status.blocker ? "bg-amber-50 border-amber-300 text-amber-700" : "border-slate-200 text-slate-500 hover:text-slate-900"
          }`}
          data-testid={`status-blocker-${status.id}`}
        >
          <WarningCircle size={12} weight="bold" /> Blocker
        </button>
      )}
      {canEdit && (
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onRemove(status.id)}
          className="h-8 w-8 rounded-md text-slate-400 hover:text-red-600 shrink-0"
          data-testid={`status-remove-${status.id}`}
        >
          <Trash size={14} weight="bold" />
        </Button>
      )}
    </div>
  );
}
